// 견적번호에 마우스를 올렸을 때 뜨는 요약 카드: 견적항목 / 공급가액 / 수량 / 공급금액
// 표시 규칙은 PDF 와 동일 (PDF 미포함 항목 제외, VAT 포함/별도 환산, 묶음 행의 텍스트·빈칸 오버라이드, 칸별 PDF 숨김)
import { STATUS_LABEL } from '../utils/versionUtils';

const MAX_ROWS = 10;
const fmt = (n) => Number(n || 0).toLocaleString();

export default function EstimateSummaryCard({ est, data, rect }) {
  const vw = window.innerWidth, vh = window.innerHeight;
  const W = Math.min(560, vw - 16);        // 휴대폰에서는 화면 폭에 맞춤
  const narrow = W < 480;
  const left = Math.max(8, Math.min(rect.left, vw - W - 8));
  const spaceBelow = vh - rect.bottom;
  const style = {
    position: 'fixed', left, width: W, zIndex: 2147482000, pointerEvents: 'none',
    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
    fontSize: 12, color: '#1e293b', overflow: 'hidden',
    ...(spaceBelow >= 260 ? { top: rect.bottom + 4 } : { bottom: vh - rect.top + 4 }),
  };

  const head = (
    <div style={{ padding: '8px 12px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, alignItems: 'center' }}>
      <b style={{ flex: 'none' }}>{est.estimate_number || est.draft_id}</b>
      <span style={{ flex: 'none', color: '#64748b' }}>v{est.version} · {STATUS_LABEL[est.status] || est.status}</span>
      {/* 최종사용자 · 사업명: 같은 줄 오른쪽 정렬, 길면 … 으로 줄임 (최종사용자가 없으면 사업명만) */}
      <span style={{ flex: '1 1 auto', minWidth: 0, textAlign: 'right', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {est.end_user ? <span style={{ fontWeight: 400, color: '#64748b' }}>{est.end_user}{est.project_name ? ' · ' : ''}</span> : null}
        {est.project_name || ''}
      </span>
    </div>
  );

  if (!data) return <div style={style}>{head}<div style={{ padding: 14, color: '#94a3b8' }}>불러오는 중...</div></div>;
  if (data.error) return <div style={style}>{head}<div style={{ padding: 14, color: '#dc2626' }}>항목을 불러오지 못했습니다.</div></div>;

  const vatSep = data.vat_separate !== false;
  const inclusiveStored = data.vat_input_mode === 'inclusive';
  const dp = (v) => (vatSep || inclusiveStored ? v : Math.round(v * 1.1));
  const items = (data.items || []).filter(it => it.show_in_pdf !== false);
  const shown = items.slice(0, MAX_ROWS);

  const rows = shown.map(it => {
    const dov = it.display_overrides || {};
    const hide = dov.hide || {};
    const upText = dov.up, qtyText = dov.qty;
    const up = upText != null ? upText : fmt(dp(parseInt(it.unit_price) || 0));
    const qty = dov.qty_unset ? '' : (qtyText != null ? qtyText : fmt(it.quantity));
    const supply = dov.qty_unset ? '' : upText != null ? (upText || '') : qtyText != null ? '' : fmt(dp(parseInt(it.unit_price) || 0) * (it.quantity || 1));
    return {
      name: it.item_model || it.item_name || '',
      up: hide.up ? '' : up, qty: hide.qty ? '' : qty, supply: hide.amt ? '' : supply,
    };
  });

  const subtotal = parseInt(data.subtotal) || 0;
  const total = parseInt(data.total_amount) || 0;
  const cell = { padding: narrow ? '4px 6px' : '4px 10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const num = { ...cell, textAlign: 'right' };

  return (
    <div style={style}>
      {head}
      <table style={{ width: '100%', tableLayout: 'fixed', fontSize: 12, borderCollapse: 'collapse' }}>
        <colgroup><col style={{ width: 28 }} /><col /><col style={{ width: narrow ? 86 : 110 }} /><col style={{ width: narrow ? 40 : 56 }} /><col style={{ width: narrow ? 92 : 120 }} /></colgroup>
        <thead>
          <tr style={{ color: '#64748b', background: '#fff' }}>
            <th style={{ ...cell, textAlign: 'center', fontWeight: 600, padding: '4px 4px' }}>No</th>
            <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>견적항목</th>
            <th style={{ ...num, fontWeight: 600 }}>공급가액</th>
            <th style={{ ...num, fontWeight: 600 }}>수량</th>
            <th style={{ ...num, fontWeight: 600 }}>공급금액</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
              <td style={{ ...cell, textAlign: 'center', color: '#94a3b8', padding: '4px 4px' }}>{i + 1}</td>
              <td style={cell}>{r.name}</td>
              <td style={num}>{r.up}</td>
              <td style={num}>{r.qty}</td>
              <td style={{ ...num, fontWeight: 600 }}>{r.supply}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={5} style={{ ...cell, textAlign: 'center', color: '#94a3b8', padding: 12 }}>항목이 없습니다.</td></tr>}
        </tbody>
      </table>
      <div style={{ padding: '6px 12px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 14, alignItems: 'center' }}>
        {items.length > MAX_ROWS && <span style={{ color: '#64748b' }}>외 {items.length - MAX_ROWS}개 항목</span>}
        <span style={{ marginLeft: 'auto', color: '#64748b' }}>{vatSep ? '합계금액' : '합계(VAT포함)'} <b style={{ color: '#1e293b' }}>₩{fmt(vatSep ? subtotal : total)}</b></span>
        {vatSep && <span style={{ color: '#64748b' }}>최종견적가(VAT포함) <b style={{ color: '#1e293b' }}>₩{fmt(total)}</b></span>}
      </div>
    </div>
  );
}
